import { FC, useRef, memo, useEffect, useState } from 'react'
import { Box, Flex, Grid } from '@/components/atoms/Grid'
import ProductDetailColorsChooser from '@/components/organisms/productDetailSideMenu/productDetailColorsChooser'
import ProductDetailSizesPicker, {
  SizesPickerActions
} from '@/components/organisms/productDetailSideMenu/productDetailSizesPicker'
import { Body2, H2, MediumTinyBold, Tiny } from '@/components/atoms/typography'
import {
  MarketCode,
  MultiDiscount,
  Product,
  ProductLabelType,
  ProductSize
} from '@/network/graphql.g'
import { Tiny2 } from '@/components/atoms/typography'
import ProductDetailSizesModalContent from './productDetailSizesModalContent'
import ProductLabel from '@/components/molecules/productLabel'
import ProductDetailTitle from './productDetailTitle'
import ProductDetailManufacturer from './productDetailManufacturer'
import { useMhub } from '@/providers/mhubProvider'
import { PictureWithPlaceholder } from '@/components/atoms/picture'
import Trans from 'next-translate/Trans'
import { Routes } from '@/core/routes'
import Link from '@/components/atoms/link'
import dynamic from 'next/dynamic'
import {
  Desktop,
  MediaContextProvider,
  Mobile,
  TabletOrMobile
} from '@/components/atoms/media'
import styled, { css, useTheme } from 'styled-components'
import ProductDetailStockDeliveryNew from '@/components/organisms/productDetailRedesign/productDetailStockDeliveryNew'
import ProductDetailSizesNew from '@/components/organisms/productDetailRedesign/productDetailSizesNew'
import ProductDetailAddToCart from '@/components/organisms/productDetailSideMenu/productDetailAddToCart'
import ProductDetailSizeTable from '@/components/organisms/productDetailSideMenu/productDetailSizeTable'
import PriceList, { PriceListLayout } from '@/components/molecules/priceList'
import { SectionSeparator } from '@/components/atoms/separator'
import { makePriceString } from '@/core/toStringHelper'
import { useProductDetail } from '@/components/organisms/productDetailSideMenu/useProductDetail'
import { useAsset } from '@/providers/assetsProvider'
import RatingInfoIcon from '@/components/molecules/ratingInfoIcon'
import { PromoCodesProvider } from '@/providers/promoCodesProvider'
import ProductDetailCouponNew from '@/components/organisms/productDetailSideMenu/productDetailCouponNew'
import useTranslation from 'next-translate/useTranslation'

import { useUser } from '@/providers/userProvider'
import { useTodayCountdown } from '../../../hooks/useDailyCountdown'
import ProductTimeCounter from '@/components/molecules/productTimeCounter'
import { transparentize } from 'polished'
import { validFor } from '@/theme/validFor'
import { useRouter } from 'next/router'
import useDevice from 'hooks/useDevice'
import { useDomain } from '@/providers/storeProvider'
import { scrollIntoViewBelowHeader } from '@/utils/scrollHelper'
import ProductDetailStickyBar from '@/components/organisms/productDetailSideMenu/productDetailStickyBar'

type ProductSideMenuActionPartProps = {
  product: Product
  modalMode: boolean
  initialSize?: ProductSize
  setHoverColorVariant: (hoverColorVariant: string) => void
  onDidTapClose?: () => void
  scrollToProductDetailRating?: () => void
  multiDiscount: MultiDiscount
  hasSizeInUrl?: boolean
  serverSelectedSize?: ProductSize
}

const DynamiAutomaticallyCouponPopup = dynamic(
  () =>
    import(
      '@/components/organisms/automaticallyCouponPopup/automaticallyCouponPopup'
    )
)

export const ProductSideMenuActionPart: FC<ProductSideMenuActionPartProps> =
  memo(
    ({
      product,
      modalMode,
      initialSize,
      setHoverColorVariant,
      onDidTapClose,
      multiDiscount,
      scrollToProductDetailRating,
      hasSizeInUrl,
      serverSelectedSize
    }) => {
      const { t } = useTranslation('common')
      const theme = useTheme()
      const { isEmployee } = useUser()

      const {
        sizes,
        colors,
        productBrandImage,
        productBrand,
        name,
        currency,
        prices,
        manufacturerUrl,
        labels,
        promoCodes
      } = product

      const {
        sizeId,
        setSizeId,
        purchaseInfo,
        setPurchaseInfo,
        selectedPromoCode,
        hasSelectedSize,
        hasStock,
        hasAllSizesStock,
        showSizesPicker,
        setShowSizesPicker,
        showAutomaticallyCouponPopup,
        setAutomaticallyCouponPopup,
        getDisplayPromoCodes,
        mainDisplayPrice,
        discountBestPrice,
        isPriceHigherWithPromo,
        onAddToCart,
        onDidChooseSize
      } = useProductDetail(product, {
        initialSize,
        serverSelectedSize,
        modalMode,
        onDidTapClose
      })

      const { isMobile } = useDevice()
      const router = useRouter()
      const { subdomainName } = useDomain()
      const sizesRef = useRef<HTMLDivElement>(null)
      const [inlineAddToCart, setInlineAddToCart] =
        useState<HTMLDivElement>(null)
      const [showSizeError, setShowSizeError] = useState<boolean>(false)
      const hasMobileStickyBar = !modalMode && sizes?.length > 0

      const onDidSelectSize = (selectedSizeId: string) => {
        const selectedSize = sizes?.find((size) => size.id === selectedSizeId)
        if (selectedSize) {
          setPurchaseInfo((prevInfo) => ({ ...prevInfo, size: selectedSize }))
          setShowSizeError(false)
        }
      }

      // On mobile, a missing size sends the customer up to the sizes instead
      // of opening the size picker.
      const onAddToCartClick = async () => {
        if (isMobile && hasMobileStickyBar && !purchaseInfo.size) {
          setShowSizeError(true)
          scrollIntoViewBelowHeader(sizesRef.current)
          return
        }
        await onAddToCart()
      }

      // The sizes grid follows the size in the URL, so the sticky bar picks
      // a size the same way the grid does.
      const onStickyBarSelectSize = (selectedSizeId: string) =>
        router.replace(
          {
            pathname: router.pathname,
            query: { ...router.query, size: selectedSizeId }
          },
          undefined,
          { shallow: true }
        )

      const { pushSizeChoose } = useMhub()

      useEffect(() => {
        sizesPickerRef?.current?.setSizeId(sizeId)
      }, [sizeId])

      const sizesPickerRef = useRef<SizesPickerActions>(null)
      const brandUrl = manufacturerUrl

      const minCount = multiDiscount?.minProductCount
      const freeCount = multiDiscount?.freeProductCount
      const minMinusFreeCount = minCount - freeCount
      const { startTime, endTime } = useTodayCountdown()

      const { useSOIPricing } = useAsset()

      const bezvaSportCZLabels = {
        labels: useSOIPricing
          ? discountBestPrice === 0 || isPriceHigherWithPromo
            ? []
            : labels.map((label) => {
                if (label.type === ProductLabelType.Discount) {
                  return {
                    ...label,
                    title: `${discountBestPrice}%`
                  }
                }
                return label
              })
          : labels
      }

      const { showReviews } = useAsset()

      const discountLabel = (
        useSOIPricing ? bezvaSportCZLabels.labels : labels
      ).find(({ type }) => type === ProductLabelType.Discount)

      const stickyBarPrice = (() => {
        if (isEmployee) {
          return makePriceString({ price: prices?.finalPrice, currency })
        }
        const priceStr = makePriceString({
          price: mainDisplayPrice.price,
          currency
        })
        if (!mainDisplayPrice.showFromPrefix) {
          return priceStr
        }
        return subdomainName === MarketCode.Hu
          ? `${priceStr}-tól`
          : `${t('Information.Postage.From')} ${priceStr}`
      })()

      const stickyBarPreviousPrice =
        !useSOIPricing && prices?.secondPrice > 0
          ? makePriceString({ price: prices.secondPrice, currency })
          : undefined

      return (
        <>
          {showSizesPicker && (
            <ProductDetailSizesModalContent
              sizes={sizes}
              onDidTapClose={() => setShowSizesPicker(false)}
              onDidChooseSize={setSizeId}
              onDidConfirm={onDidChooseSize}
              initialSelectedSizeId={sizeId}
            />
          )}
          <Flex
            justifyContent="space-between"
            alignItems="center"
            mt={{ mobile: 20, tablet: 0, desktop: 0 }}
          >
            <ProductDetailManufacturer
              productBrandImage={productBrandImage}
              productBrand={productBrand}
              brandUrl={brandUrl}
            />
            {showReviews ? (
              <StyledButton
                onClick={scrollToProductDetailRating}
                modalMode={modalMode}
              >
                {product.productRateData?.canShow ? (
                  <RatingInfoIcon
                    productRateData={product?.productRateData}
                    hideRateCounts
                    marginTop={2}
                    fillColor={theme.colors.sizeTableDescription}
                  />
                ) : null}
              </StyledButton>
            ) : null}
          </Flex>
          <ProductDetailTitle
            name={name}
            productBrand={productBrand}
            brandUrl={brandUrl}
          />
          {useSOIPricing ? (
            <StyledProductLabel
              label={bezvaSportCZLabels?.labels.find(
                ({ type }) => type === ProductLabelType.Discount
              )}
            />
          ) : (
            <StyledProductLabel
              label={labels.find(
                ({ type }) => type === ProductLabelType.Discount
              )}
              isDetail={true}
            />
          )}
          {prices && (
            <Flex
              flexDirection={useSOIPricing ? 'column' : 'row'}
              justifyContent="space-between"
              mb={12}
            >
              {useSOIPricing ? (
                <>
                  <Flex justifyContent="space-between" alignItems="flex-end">
                    <Flex alignItems="flex-end">
                      {!hasSizeInUrl || hasSelectedSize ? (
                        <Trans
                          i18nKey="common:ProductDetail.PriceList.realPrice"
                          components={[
                            <FirstPriceBig />,
                            <FirstBigPriceText />
                          ]}
                          values={{
                            price: (() => {
                              const priceStr = makePriceString({
                                price: mainDisplayPrice.price,
                                currency
                              })
                              return mainDisplayPrice.showFromPrefix
                                ? `${t('Information.Postage.From')} ${priceStr}`
                                : priceStr
                            })()
                          }}
                        />
                      ) : null}
                    </Flex>
                  </Flex>
                  <SectionSeparator my="4px" />
                  <PriceList
                    layout={PriceListLayout.big}
                    prices={prices}
                    currency={currency}
                    showClasses={true}
                    hasSelectedSize={hasSelectedSize}
                    hasStock={hasStock}
                    hasAllSizesStock={hasAllSizesStock}
                    hasSizeInUrl={hasSizeInUrl}
                    selectedPromoCodePrice={selectedPromoCode?.discountAmount}
                    fromPriceWhenNoSize={(() => {
                      const displayCodes = getDisplayPromoCodes()
                      const count = displayCodes.length
                      return count >= 2
                        ? Math.min(
                            ...displayCodes.map((c) => c?.discountAmount || 0)
                          )
                        : prices?.promoCodePrice ?? prices?.finalPrice
                    })()}
                    promoCodesCount={getDisplayPromoCodes().length}
                  />
                </>
              ) : null}
              {!useSOIPricing ? (
                <PriceList
                  layout={PriceListLayout.big}
                  prices={prices}
                  currency={currency}
                  showClasses={true}
                  hasSelectedSize={hasSelectedSize}
                  hasStock={hasStock}
                  hasAllSizesStock={hasAllSizesStock}
                  hasSizeInUrl={hasSizeInUrl}
                  selectedPromoCodePrice={selectedPromoCode?.discountAmount}
                  fromPriceWhenNoSize={(() => {
                    const displayCodes = getDisplayPromoCodes()
                    const count = displayCodes.length
                    return count >= 2
                      ? Math.min(
                          ...displayCodes.map((c) => c?.discountAmount || 0)
                        )
                      : prices?.promoCodePrice ?? prices?.finalPrice
                  })()}
                  promoCodesCount={getDisplayPromoCodes().length}
                />
              ) : null}
            </Flex>
          )}
          {colors?.length > 0 && (
            <MediaContextProvider>
              <Desktop>
                <ProductDetailColorsChooser
                  colors={colors}
                  productBrand={productBrand}
                  mainCategoryId={product?.mainCategory?.id}
                  currentImage={product.id}
                  setHoverColorVariant={setHoverColorVariant}
                  numberOfSlides={5.5}
                />
              </Desktop>
              <TabletOrMobile>
                <ProductDetailColorsChooser
                  colors={colors}
                  productBrand={productBrand}
                  mainCategoryId={product?.mainCategory?.id}
                  currentImage={product.id}
                  setHoverColorVariant={setHoverColorVariant}
                  numberOfSlides={4.5}
                />
              </TabletOrMobile>
            </MediaContextProvider>
          )}
          {promoCodes.length > 0 && !isEmployee ? (
            <PromoCodesProvider promoCodes={promoCodes} prices={prices}>
              <ProductDetailCouponNew
                promoCodes={getDisplayPromoCodes()}
                modalMode={modalMode}
              />
            </PromoCodesProvider>
          ) : null}
          {multiDiscount &&
          !isEmployee &&
          Object.values(multiDiscount).some((value) => value !== null) ? (
            <>
              <Flex justifyContent="flex-end" mt={15}>
                <StyledCouponCountdownWrapper>
                  <ProductTimeCounter startTime={startTime} endTime={endTime} />
                </StyledCouponCountdownWrapper>
              </Flex>
              <MultiDiscountWrapper data-testid="productDetailMultiDiscount">
                <CountAmount>{`${minMinusFreeCount} + ${freeCount}`}</CountAmount>
                <MultiDiscountInfoWrapper flexDirection="column">
                  <Flex my="5px">
                    <Trans
                      i18nKey="common:ProductDetail.Multidiscount.multiDiscountTitle"
                      components={[
                        <PrefixMultiDiscountTitle />,
                        <MultiDiscountTitle />
                      ]}
                      values={{
                        freeCount: freeCount,
                        minMinusFreeCount: minMinusFreeCount
                      }}
                    />
                  </Flex>
                  <Trans
                    i18nKey="common:ProductDetail.Multidiscount.multiDiscountSubtitle"
                    components={[<SubMultiDiscountTitle />]}
                    values={{
                      minCount: minCount,
                      freeCount: freeCount,
                      minMinusFreeCount: minMinusFreeCount
                    }}
                  />
                </MultiDiscountInfoWrapper>
              </MultiDiscountWrapper>
            </>
          ) : null}

          {!product?.giftBoxAvailable && product?.pillowBoxAvailable != null && (
            <Box mb={12}>
              <Link
                href={`${Routes.giftsProducts}/${product?.pillowBoxAvailable}`}
              >
                <StyledBox p="8px">
                  <GiftsText>
                    <Trans
                      i18nKey="common:Gifts.bannerText"
                      components={[<span />, <StyledSpan />]}
                    />
                  </GiftsText>
                </StyledBox>
                <PictureWithPlaceholder
                  src="/gifts/banner-darcek-set-2.jpg"
                  ratio={99 / 950}
                />
              </Link>
            </Box>
          )}
          {modalMode ? (
            <Flex my={2} justifyContent="end">
              {!product?.disableSizeChart && (
                <ProductDetailSizeTable
                  productId={product?.id}
                  productBrand={productBrand}
                  name={product?.name}
                  supplierId={product?.MHubProperties?.supplierId}
                  category={product?.MHubProperties?.category}
                />
              )}
            </Flex>
          ) : null}
          {modalMode ? (
            <ProductDetailSizesPicker
              mt="8px"
              ref={sizesPickerRef}
              initialSelectedSizeId={sizeId}
              sizes={sizes}
              onDidSelectSize={(size: ProductSize) => {
                size?.value && pushSizeChoose(size?.value)
                setPurchaseInfo({ ...purchaseInfo, size })
              }}
              productId={product?.id}
              promoCodes={promoCodes}
            />
          ) : null}
          {!modalMode && sizes && (
            <Box mt="24px" mb="20px" ref={sizesRef}>
              <ProductDetailSizesNew
                product={product}
                sizes={sizes}
                initialSelectedSizeId={sizeId}
                onDidSelectSize={onDidSelectSize}
                promoCodes={promoCodes}
                hasError={showSizeError}
              />
            </Box>
          )}
          {!modalMode && sizes && (
            <StockDeliveryRow>
              <ProductDetailStockDeliveryNew
                sizes={sizes}
                selectedSize={purchaseInfo?.size}
              />
            </StockDeliveryRow>
          )}
          <Box ref={setInlineAddToCart}>
            <ProductDetailAddToCart
              my={14}
              product={product}
              addImmediately={!!purchaseInfo.size}
              onAddToCartAction={onAddToCartClick}
            />
          </Box>
          {hasMobileStickyBar && (
            <Mobile>
              <ProductDetailStickyBar
                inlineButton={inlineAddToCart}
                sizes={sizes}
                selectedSize={purchaseInfo.size}
                onSelectSize={onStickyBarSelectSize}
                onAddToCart={onAddToCartClick}
                price={stickyBarPrice}
                previousPrice={stickyBarPreviousPrice}
                discountLabel={discountLabel}
              />
            </Mobile>
          )}
          {showAutomaticallyCouponPopup && (
            <DynamiAutomaticallyCouponPopup
              setShowAutomaticallyCouponPopup={setAutomaticallyCouponPopup}
              showAutomaticallyCouponPopup={showAutomaticallyCouponPopup}
            />
          )}
        </>
      )
    }
  )

const StockDeliveryRow = styled(Box)`
  margin-bottom: 4px;
`

const GiftsText = styled(Tiny2)`
  margin: 0;
`

const StyledSpan = styled.span`
  &:before {
    content: ' ';
  }
`

const StyledBox = styled(Box)`
  background-color: #f1f1f1;
`

const StyledProductLabel = styled(ProductLabel)`
  height: 24px;
  & > p {
    margin: 0;
  }
  font-size: 16px;
`

const FirstPriceBase = css`
  color: ${(props) => props.theme.colors.accent};
  text-overflow: ellipsis;
  margin-top: 0;
  margin-bottom: 0;
`

const FirstPriceBig = styled(H2)`
  ${FirstPriceBase}
  text-align: ${({ priceTittle }) => (priceTittle ? 'center' : 'left')};
  ${({ priceTittle }) => priceTittle && 'line-height: 10px;'}
`

const FirstBigPriceText = styled(Tiny)`
  margin: 0 0 0 8px;
  line-height: 1.9;
  font-weight: 400;
  color: ${(props) => props.theme.colors.onBackground};
  word-spacing: 1px;
`

const MultiDiscountWrapper = styled(Flex)`
  padding: 10px 0 12px 0;
  border-radius: 6px 0 6px 6px;
  background-color: ${(props) =>
    transparentize(0.8, props.theme.colors.multiDiscountColor)};
`

const CountAmount = styled(Body2)`
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.accent};
  padding: 5px 7px;
  border: 1px solid ${(props) => props.theme.colors.accent};
  border-radius: 6px;
  margin: 0 0 0 12px;
  align-self: center;
  white-space: nowrap;
  display: inline-block;
`

const MultiDiscountInfoWrapper = styled(Flex)`
  margin-left: 12px;
`

const PrefixMultiDiscountTitle = styled(MediumTinyBold)`
  color: ${(props) => props.theme.colors.accent};
  margin: 0;
  ${validFor.desktop`
    font-size: 14px;
  `}
  ${validFor.tablet`
    font-size: 14px;
  `}
  ${validFor.mobile`
    font-size: 12px;
  `}
`

const MultiDiscountTitle = styled(MediumTinyBold)`
  color: ${(props) => props.theme.colors.onBackground};
  margin: 0 0 0 3px;
  ${validFor.desktop`
    font-size: 14px;
  `}
  ${validFor.tablet`
    font-size: 14px;
  `}
  ${validFor.mobile`
    font-size: 12px;
  `}
`

const SubMultiDiscountTitle = styled(MediumTinyBold)`
  color: ${(props) => transparentize(0.4, props.theme.colors.onBackground)};
  margin: 0;
  padding-right: 8px;
  font-weight: 400;
  line-height: 1.5;
  ${validFor.desktop`
    font-size: 12px;
  `}
  ${validFor.tablet`
    font-size: 12px;
  `}
`

const StyledCouponCountdownWrapper = styled(Grid)`
  background-color: ${(props) => props.theme.colors.accent};
  color: ${(props) => props.theme.colors.background};
  grid-template-columns: auto;
  grid-template-rows: auto;
  align-items: center;
  grid-gap: 10px;
  width: 130px;
  padding-top: 1px;
  margin-bottom: -1px;
`

const StyledButton = styled.button`
  border: none;
  outline: none;
  background: inherit;
  cursor: ${({ modalMode }) => (!modalMode ? 'pointer' : 'unset')};
  padding: 0;
  padding-right: ${({ modalMode }) => (modalMode ? '15px' : 0)};
  color: ${(props) => props.theme.colors.onBackground};
`
